// ============================================================
// CommandBar — Square Code shell command surface.
//
// Three input paths, one execution path:
//
//   1. Typed text → useCommandDispatch.execute(input)
//   2. Voice in (Gladia STT via /api/voice/transcribe inline mode)
//      → useVoiceCapture.stopAndTranscribe()
//      → useCommandDispatch.execute(transcript)
//   3. Same envelope, same /api/commands/dispatch route, same
//      sq_commands realtime stream — voice does not get a side
//      door.
//
// Voice out is the browser SpeechSynthesis API only (first pass).
// It is opt-in via the speaker button, persisted to localStorage,
// and never blocks command execution. When a command terminates,
// the bar speaks a short honest summary derived from the actual
// dispatcher result — never a synthesized response the backend
// did not produce.
// ============================================================

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { SHELL_TOKENS, shellStyles } from './styles'
import { useCommandDispatch } from '../hooks/useCommandDispatch'
import { useVoiceCapture } from '../hooks/useVoiceCapture'
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis'
import type { CommandApiResult } from '../api/client'
import { useWorkbench } from './workbench/WorkbenchContext'
import { useCommandContext } from './workbench/useCommandContext'

function isTerminalStatus(status: string): boolean {
  return (
    status === 'done' ||
    status === 'error' ||
    status === 'cancelled' ||
    status === 'unknown'
  )
}

/**
 * Build a short, honest spoken summary from the dispatcher result.
 * Never invents content — only restates the status + the most
 * useful field the backend actually returned.
 */
function summarizeForSpeech(result: CommandApiResult): string {
  if (!result) return ''
  switch (result.status) {
    case 'done': {
      const navigateTo = typeof result.result?.navigate === 'string' ? (result.result.navigate as string) : null
      if (navigateTo) {
        // Map common shell routes to short utterances; fall back to a generic phrase.
        if (/\/projects\/[^/]+\/studio$/.test(navigateTo)) return 'Opening studio.'
        if (/\/projects\/[^/]+\/site$/.test(navigateTo)) return 'Opening site.'
        if (/\/projects\/[^/]+\/hosting$/.test(navigateTo)) return 'Opening hosting.'
        if (/\/projects\/[^/]+/.test(navigateTo)) return 'Opening project.'
        if (/\/olivium\//.test(navigateTo)) return 'Opening Olivium.'
        if (/\/workspace$/.test(navigateTo) || /\/quamspace$/.test(navigateTo)) return 'Opening workspace.'
        return 'Done.'
      }
      if (result.message) return result.message.slice(0, 200)
      return 'Done.'
    }
    case 'dispatched':
      return result.message?.slice(0, 200) || 'Command dispatched.'
    case 'running':
      return 'Command running.'
    case 'error':
      return 'Command failed.'
    case 'unknown':
      return 'No matching command.'
    case 'cancelled':
      return 'Command cancelled.'
    default:
      return ''
  }
}

const pillPrimary: CSSProperties = {
  padding: '6px 14px',
  borderRadius: 8,
  cursor: 'pointer',
  background: SHELL_TOKENS.brandSoft,
  border: `1px solid ${SHELL_TOKENS.brandLine}`,
  color: SHELL_TOKENS.brand,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.06em',
  fontFamily: 'var(--font-mono)',
}

const pillGhost: CSSProperties = {
  padding: '6px 14px',
  borderRadius: 8,
  cursor: 'pointer',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.10)',
  color: SHELL_TOKENS.inkMuted,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  fontFamily: 'var(--font-mono)',
}

export default function CommandBar() {
  const [value, setValue] = useState('')
  const { state, execute, dispatchProposal, clear } = useCommandDispatch()
  const voice = useVoiceCapture()
  const tts = useSpeechSynthesis()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const workbench = useWorkbench()
  const { context } = useCommandContext()

  // Carry an un-runnable request into the governed agent runtime. Opens the
  // persistent Agent Workbench in compose mode with the current context — the
  // router recommends an agent + skills, the owner confirms, and a REAL
  // canonical task is created (no sessionStorage prefill, no second runtime).
  const sendToAgent = useCallback(
    (text: string) => {
      const request = (text || '').trim()
      if (!request) return
      clear()
      setValue('')
      workbench.startAgentFlow(request, context)
    },
    [clear, workbench, context],
  )

  const runProposal = useCallback(async () => {
    if (state.interpretation?.kind !== 'proposal') return
    const result = await dispatchProposal(state.interpretation.intent)
    if (result) setValue('')
  }, [dispatchProposal, state.interpretation])

  // Multimodal dock handoff (v21): the dock's command_bar modes fire
  // sc-command-focus; we focus the input and apply any prefill.
  // Additive only — text/voice dispatch is untouched.
  useEffect(() => {
    const onFocusRequest = (e: Event) => {
      const detail = (e as CustomEvent<{ prefill?: string }>).detail
      if (detail?.prefill) setValue(detail.prefill)
      inputRef.current?.focus()
    }
    window.addEventListener('sc-command-focus', onFocusRequest)
    return () => window.removeEventListener('sc-command-focus', onFocusRequest)
  }, [])

  // Speak terminal results once per command id. We track the
  // last spoken id so realtime updates do not double-speak.
  const lastSpokenIdRef = useRef<string | null>(null)
  useEffect(() => {
    const result = state.result
    if (!result) return
    if (!isTerminalStatus(result.status)) return
    if (lastSpokenIdRef.current === result.id) return
    lastSpokenIdRef.current = result.id
    const summary = summarizeForSpeech(result)
    if (summary) tts.speak(summary)
  }, [state.result, tts])

  const run = useCallback(async () => {
    const command = value.trim()
    if (!command) return
    // An explicit @agent mention goes straight to the governed agent runtime
    // (the workbench router), not the deterministic command dispatcher.
    if (/^@[a-z]+/i.test(command)) {
      sendToAgent(command)
      return
    }
    const result = await execute(command)
    if (result) setValue('')
  }, [execute, value, sendToAgent])

  const handleMicClick = useCallback(async () => {
    if (voice.state.status === 'recording') {
      const transcript = await voice.stopAndTranscribe()
      if (!transcript) return
      setValue(transcript)
      await execute(transcript)
      setValue('')
    } else if (voice.state.status === 'idle' || voice.state.status === 'error') {
      await voice.start()
    }
  }, [execute, voice])

  const recording = voice.state.status === 'recording'
  const transcribing = voice.state.status === 'transcribing'
  const dispatching = state.status === 'dispatching'

  const micLabel =
    !voice.state.available
      ? '·'
      : recording
        ? '■'
        : transcribing
          ? '…'
          : '●'

  const micColor = !voice.state.available
    ? SHELL_TOKENS.inkFaint
    : recording
      ? SHELL_TOKENS.red
      : transcribing
        ? SHELL_TOKENS.amber
        : SHELL_TOKENS.brand

  const interp = state.interpretation

  return (
    <div style={shellStyles.commandBar}>
      {/* Compass interpretation panel — appears above the bar when a
          typed request was not a deterministic command. Every branch is
          a visible, actionable result: run the interpreted command, read
          the answer, or carry it to the agent lane. Never a dead end. */}
      {interp && (
        <div
          role="status"
          style={{
            position: 'absolute',
            left: 60,
            right: 20,
            bottom: 'calc(100% + 6px)',
            padding: '12px 14px',
            borderRadius: 12,
            background: 'rgba(16, 8, 28, 0.98)',
            border: `1px solid ${
              interp.kind === 'needs_setup' ? SHELL_TOKENS.amber : SHELL_TOKENS.brandLine
            }`,
            boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
            backdropFilter: 'blur(20px)',
            zIndex: 40,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6,
            }}
          >
            <span
              style={{
                fontSize: 9,
                letterSpacing: '0.16em',
                fontWeight: 800,
                textTransform: 'uppercase',
                fontFamily: 'var(--font-mono)',
                color:
                  interp.kind === 'proposal'
                    ? SHELL_TOKENS.cyan
                    : interp.kind === 'answer'
                      ? SHELL_TOKENS.brand
                      : interp.kind === 'needs_setup'
                        ? SHELL_TOKENS.amber
                        : SHELL_TOKENS.inkFaint,
              }}
            >
              {interp.kind === 'proposal'
                ? '✦ Interpreted command'
                : interp.kind === 'answer'
                  ? '✦ Compass'
                  : interp.kind === 'needs_setup'
                    ? '⚑ Setup required'
                    : '✦ Not a command'}
            </span>
            <button
              type="button"
              onClick={() => clear()}
              aria-label="dismiss"
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                color: SHELL_TOKENS.inkFaint,
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'var(--font-mono)',
              }}
            >
              ✕
            </button>
          </div>

          {interp.kind === 'proposal' ? (
            <>
              <div style={{ fontSize: 12.5, color: SHELL_TOKENS.inkDim, lineHeight: 1.6, marginBottom: 10 }}>
                {interp.intent.message ? interp.intent.message + ' ' : ''}
                <span style={{ color: SHELL_TOKENS.inkFaint, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                  → {interp.intent.command_id}
                  {interp.intent.confidence === 'low' ? ' · low confidence' : ''}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => void runProposal()} style={pillPrimary}>
                  Run this
                </button>
                <button type="button" onClick={() => sendToAgent(state.lastInput ?? '')} style={pillGhost}>
                  Send to agent instead
                </button>
              </div>
            </>
          ) : interp.kind === 'answer' ? (
            <div style={{ fontSize: 12.5, color: SHELL_TOKENS.inkDim, lineHeight: 1.65 }}>{interp.message}</div>
          ) : (
            <>
              <div style={{ fontSize: 12.5, color: SHELL_TOKENS.inkDim, lineHeight: 1.6, marginBottom: 10 }}>
                {interp.message}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => sendToAgent(state.lastInput ?? '')} style={pillPrimary}>
                  Send to Agent Workspace →
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          flexShrink: 0,
          background: 'linear-gradient(135deg, #1a1028, #0d0a14)',
          border: `1px solid ${SHELL_TOKENS.brandLine}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 0 12px ${SHELL_TOKENS.brandGlow}`,
        }}
      >
        <span
          style={{
            fontSize: 16,
            fontWeight: 900,
            color: SHELL_TOKENS.brand,
            textShadow: `0 0 10px ${SHELL_TOKENS.brandGlow}`,
            fontFamily: 'var(--font-brand)',
          }}
        >
          Q
        </span>
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 14px',
          borderRadius: 9,
          background: SHELL_TOKENS.surfaceRaised,
          border: `1px solid ${recording ? SHELL_TOKENS.red : SHELL_TOKENS.brandLine}`,
          minHeight: 36,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: SHELL_TOKENS.brand,
            fontFamily: 'var(--font-brand)',
          }}
        >
          ›
        </span>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void run()
          }}
          placeholder={recording ? 'Listening…' : transcribing ? 'Transcribing…' : 'Computer, execute...'}
          disabled={recording || transcribing}
          style={{
            flex: 1,
            background: 'none',
            border: 'none',
            outline: 'none',
            color: SHELL_TOKENS.ink,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'inherit',
            height: 36,
          }}
        />
        {state.status !== 'idle' && (
          <span
            style={{
              fontSize: 10,
              color:
                state.status === 'error'
                  ? SHELL_TOKENS.red
                  : state.status === 'running' || state.status === 'dispatching'
                    ? SHELL_TOKENS.amber
                    : SHELL_TOKENS.brand,
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {state.status}
          </span>
        )}
      </div>

      {/* Mic — Gladia voice input */}
      <button
        type="button"
        onClick={() => void handleMicClick()}
        disabled={!voice.state.available || transcribing || dispatching}
        aria-label="Voice command"
        aria-pressed={recording ? 'true' : 'false'}
        title={
          !voice.state.available
            ? 'voice capture unavailable in this browser'
            : recording
              ? 'stop and transcribe'
              : 'start voice command'
        }
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          cursor: voice.state.available && !transcribing && !dispatching ? 'pointer' : 'not-allowed',
          background: recording ? 'rgba(220,38,38,0.12)' : SHELL_TOKENS.surface,
          border: `1px solid ${recording ? SHELL_TOKENS.red : SHELL_TOKENS.brandLine}`,
          color: micColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontFamily: 'var(--font-mono)',
          opacity: !voice.state.available ? 0.4 : 1,
        }}
      >
        {micLabel}
      </button>

      {/* Speaker — browser SpeechSynthesis toggle */}
      <button
        type="button"
        onClick={() => tts.toggle()}
        disabled={!tts.available}
        title={
          !tts.available
            ? 'browser speech synthesis unavailable'
            : tts.enabled
              ? 'voice output on'
              : 'voice output off'
        }
        aria-label="Voice output"
        aria-pressed={tts.enabled ? 'true' : 'false'}
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          cursor: tts.available ? 'pointer' : 'not-allowed',
          background: tts.enabled ? SHELL_TOKENS.brandSoft : SHELL_TOKENS.surface,
          border: `1px solid ${SHELL_TOKENS.brandLine}`,
          color: tts.available && tts.enabled ? SHELL_TOKENS.brand : SHELL_TOKENS.inkFaint,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontFamily: 'var(--font-mono)',
          fontWeight: 800,
          opacity: tts.available ? 1 : 0.4,
        }}
      >
        {tts.available && tts.enabled ? '◉' : '◌'}
      </button>

      <button
        type="button"
        onClick={() => void run()}
        disabled={!value.trim() || dispatching}
        style={{
          padding: '0 20px',
          borderRadius: 9,
          height: 36,
          cursor: value.trim() && !dispatching ? 'pointer' : 'not-allowed',
          background: value.trim() ? SHELL_TOKENS.brandSoft : SHELL_TOKENS.surface,
          border: `1px solid ${SHELL_TOKENS.brandLine}`,
          color: SHELL_TOKENS.brand,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          display: 'flex',
          alignItems: 'center',
          fontFamily: 'var(--font-mono)',
          opacity: value.trim() ? 1 : 0.5,
        }}
      >
        {dispatching ? 'Sending' : 'Execute'}
      </button>

      {(state.message || voice.state.error) && (
        <div
          style={{
            position: 'absolute',
            left: 72,
            right: 220,
            bottom: -20,
            fontSize: 10,
            color:
              voice.state.error || state.status === 'error'
                ? SHELL_TOKENS.red
                : SHELL_TOKENS.inkMuted,
            fontFamily: 'var(--font-mono)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {voice.state.error ?? state.message}
        </div>
      )}
    </div>
  )
}
